import { useEffect } from "react";

import { SYSTEM_FONTS } from "../screens/Dashboard/SubComponents/Studio/Components/ModalWidgets/Components/fonts.constants";

const ATTACHED = new Set<string>();

/**
 * Inject the Google Fonts `<link>` tag for `fontFamily` into the document
 * head. Idempotent — same family is only requested once per page load.
 * Pass the raw `font-family` CSS value (e.g. "Inter, system-ui, sans-serif");
 * only the first token is used. System fonts are skipped. Returns the
 * extracted primary family name (useful when the caller needs to await
 * `document.fonts.load(...)`), or `null` when nothing was injected.
 *
 * Exported so non-React callers (Pixi renderers, etc.) can ensure a font
 * is loaded before rasterising glyphs — without this the Pixi text falls
 * back to the system default and renders at different widths than the
 * DOM, causing position/size drift between the two views.
 */
export const loadGoogleFont = (fontFamily: string): string | null => {
  if (typeof document === "undefined") return null;
  if (!fontFamily) return null;
  const primary = fontFamily
    .split(",")[0]
    ?.trim()
    .replace(/^["']|["']$/g, "");
  if (!primary) return null;
  if ((SYSTEM_FONTS as readonly string[]).includes(primary)) return primary;

  const fontId = `google-font-${primary.replace(/\s+/g, "-").toLowerCase()}`;
  if (ATTACHED.has(fontId)) return primary;
  if (document.getElementById(fontId)) {
    ATTACHED.add(fontId);
    return primary;
  }

  const link = document.createElement("link");
  link.id = fontId;
  link.rel = "stylesheet";
  // Load the full weight range Google Fonts will serve (some families
  // only have a subset — Google silently drops the missing ones). This
  // covers everything from `font-weight: 300` (light) up to `900`
  // (black) so seeds that ask for unusual weights don't fall back to
  // a different family.
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    primary,
  )}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
  ATTACHED.add(fontId);
  return primary;
};

/**
 * Ensure the Google Font is loaded AND the browser has decoded the
 * face before resolving. Use in async paths (Pixi renderers) that need
 * to wait for actual glyph metrics — `document.fonts.load` resolves
 * once the requested family/size/weight combo is ready to paint.
 * Resolves immediately for system fonts.
 *
 * Passing `fontWeight` is critical for parity: without it, the browser
 * resolves to the regular (400) face and `document.fonts.load` doesn't
 * wait for the bold (700) variant. If Pixi then rasterises bold text
 * before the 700 face is decoded, Canvas2D synthesises FAUX-BOLD —
 * which renders visibly different (typically thinner) than the real
 * 700 glyph the DOM is showing. Specifying the weight closes that
 * synthesis window.
 */
export const ensureFontReady = async (
  fontFamily: string | undefined,
  fontSize: number,
  fontWeight: string | number = 400,
): Promise<void> => {
  if (typeof document === "undefined" || !fontFamily) return;
  const primary = loadGoogleFont(fontFamily);
  if (!primary) return;
  if ((SYSTEM_FONTS as readonly string[]).includes(primary)) return;
  try {
    // `document.fonts.load` triggers a load (if not already loaded) and
    // resolves when the face is ready. Format is the CSS `font`
    // shorthand: `<weight> <size> <family>` — weight first, then size.
    await document.fonts.load(`${fontWeight} ${fontSize}px "${primary}"`);
  } catch {
    /* swallow — fall back to whatever the system gives us */
  }
};

/**
 * React hook wrapper around `loadGoogleFont`. Use in components that
 * render text with a configurable family. The font load is fire-and-
 * forget — pair with `ensureFontReady` if you need to await readiness.
 */
export const useGoogleFont = (fontFamily: string | undefined | null): void => {
  useEffect(() => {
    if (fontFamily) loadGoogleFont(fontFamily);
  }, [fontFamily]);
};

export default useGoogleFont;
